// Typed fetch wrappers for the FastAPI backend. All calls are same-origin (Caddy
// serves the SPA and proxies /api to the backend), so cookies (the session, once
// auth lands in Phase 3) ride along with credentials: "include".
import type {
  AdminUser,
  ArCustomer,
  Book,
  InsurerInfo,
  CarriedMatchPair,
  CarriedNote,
  CarriedSheet,
  FrameRecords,
  ManualMatchPair,
  MultiPreviewEntry,
  ReleasedGroup,
  SaveCheck,
  SavedMatchItem,
  ReconcileResponse,
  RollforwardResponse,
  FinalizeResponse,
  ReconResult,
  ReconSettings,
} from "../types/api";

const BASE = "/api";

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = "ApiError";
  }
}

// A 401 on any call means the session expired/was revoked mid-use. The app registers a
// handler here so it can drop back to the login screen (except for /me and /login, which
// legitimately 401 during the auth check itself).
let on401: (() => void) | null = null;
export function setOn401(fn: () => void) { on401 = fn; }

async function jsonOrThrow<T>(res: Response, path?: string): Promise<T> {
  if (!res.ok) {
    if (res.status === 401 && on401 && path !== "/me" && path !== "/login") on401();
    let detail = res.statusText;
    try {
      const body = await res.json();
      detail = body?.detail ?? detail;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, detail);
  }
  return res.json() as Promise<T>;
}

async function getJson<T>(path: string): Promise<T> {
  return jsonOrThrow<T>(await fetch(BASE + path, { credentials: "include" }), path);
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  return jsonOrThrow<T>(
    await fetch(BASE + path, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
    path
  );
}

async function postForm<T>(path: string, form: FormData, signal?: AbortSignal): Promise<T> {
  return jsonOrThrow<T>(
    await fetch(BASE + path, { method: "POST", credentials: "include", body: form, signal }),
    path
  );
}

// Binary (blob) endpoints can't reuse jsonOrThrow, but their FAILURE bodies are
// still JSON with a server `detail` (e.g. 409 "prematch expired; re-run reconcile") —
// parse it so export errors are actionable instead of a bare statusText.
async function blobOrThrow(res: Response): Promise<Blob> {
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      detail = body?.detail ?? detail;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, detail);
  }
  return res.blob();
}

async function deleteJson<T>(path: string, body: unknown): Promise<T> {
  return jsonOrThrow<T>(
    await fetch(BASE + path, {
      method: "DELETE",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
    path
  );
}

async function putJson<T>(path: string, body: unknown): Promise<T> {
  return jsonOrThrow<T>(
    await fetch(BASE + path, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
    path
  );
}

// --- the per-insurer STORE scope -------------------------------------------------
// The remembered-match / released / carried / rejection stores are keyed per BOOK, and
// on AR additionally per CUSTOMER: a remembered pair's row signatures belong to ONE
// customer's ledger, so replaying them against another customer's rows would be a false
// match. The server refuses an unscoped AR store call (422) rather than silently falling
// back to the AP team's files.
//
// Held here as one module-level value rather than threaded through every grid: it is a
// property of the reconciliation currently on screen, and no grid should have to know
// it. Set from the /reconcile response (which reports both), and CLEARED on logout and
// on a book switch so one run's scope can never leak into the next.
let _storeScope: { recon_book?: Book; customer_key?: string } = {};

export function setStoreScope(scope: { book?: Book; customerKey?: string } | null): void {
  _storeScope = scope
    ? { recon_book: scope.book, customer_key: scope.customerKey || undefined }
    : {};
}

/** Scope as query params, for the GET store routes. */
function scopeQs(): string {
  const q = new URLSearchParams();
  if (_storeScope.recon_book) q.set("recon_book", _storeScope.recon_book);
  if (_storeScope.customer_key) q.set("customer_key", _storeScope.customer_key);
  const s = q.toString();
  return s ? "&" + s : "";
}

/** Scope as body fields, for the POST/DELETE store routes. Omitted when unset, so an
 *  AP request body is exactly what it was before the book axis existed. */
function scopeBody(): Record<string, string> {
  const out: Record<string, string> = {};
  if (_storeScope.recon_book) out.recon_book = _storeScope.recon_book;
  if (_storeScope.customer_key) out.customer_key = _storeScope.customer_key;
  return out;
}

// --- the display SEGMENT ---------------------------------------------------------
// Which slice of the reconciliation is on screen (backend/segments.py: Salama's Group
// Life / Non-Group Life). Module-level for the same reason as the store scope: every
// route that re-finalizes (a manual match, a note, a restore, an export) must return the
// SAME slice the screen shows, and no grid should have to know it. "" = All business,
// sent as nothing at all, so an insurer without segments sends exactly what it did before.
let _viewSegment = "";

export function setViewSegment(segment: string): void {
  _viewSegment = segment || "";
}

export function viewSegment(): string {
  return _viewSegment;
}

function segmentBody(): { segment?: string } {
  return _viewSegment ? { segment: _viewSegment } : {};
}

export const api = {
  ApiError,

  // --- auth ---
  // `books` is which reconciliation books this account may work in ("AP" / "AR"). A
  // single-book account never sees a book picker, so an AP-only user's app looks exactly
  // as it did before the AR book existed.
  me: () => getJson<{ username: string; role: string; password_unchanged: boolean;
                      can_use_gemini: boolean; books?: Book[] }>("/me"),
  login: (username: string, password: string) =>
    postJson<{ username: string }>("/login", { username, password }),
  logout: () => postJson<{ ok: boolean }>("/logout", {}),
  changePassword: (currentPassword: string, newPassword: string) =>
    postJson<{ ok: boolean }>("/account/password", {
      current_password: currentPassword, new_password: newPassword,
    }),

  // --- superadmin user management (require_admin on the backend; 403 for non-admins) ---
  adminListUsers: () =>
    getJson<{
      users: AdminUser[]; all_insurers: string[]; testing_insurers: string[];
      // The book vocabulary, plus which insurers are onboarded in each book, so the
      // panel can show onboarding coverage without guessing.
      books?: Book[];
      insurers_by_book?: Record<string, string[]>;
      testing_insurers_by_book?: Record<string, string[]>;
    }>("/admin/users"),
  adminCreateUser: (args: {
    username: string; password: string; role?: string;
    allowed_insurers?: string[] | null; can_use_gemini?: boolean;
    can_use_testing_insurers?: boolean; allowed_books?: Book[] | null;
  }) => postJson<{ username: string }>("/admin/users", args),
  adminResetPassword: (username: string, newPassword: string) =>
    postJson<{ ok: boolean }>("/admin/users/reset-password", {
      username, new_password: newPassword,
    }),
  adminSetInsurers: (username: string, allowedInsurers: string[] | null) =>
    putJson<{ ok: boolean; allowed_insurers: string[] | null }>(
      "/admin/users/insurers",
      { username, allowed_insurers: allowedInsurers }
    ),
  // null = the default book only (AP), which is what every account predating the AR
  // book reads as.
  adminSetBooks: (username: string, allowedBooks: Book[] | null) =>
    putJson<{ ok: boolean; allowed_books: Book[] }>(
      "/admin/users/books",
      { username, allowed_books: allowedBooks }
    ),
  adminSetGemini: (username: string, canUseGemini: boolean) =>
    putJson<{ ok: boolean; can_use_gemini: boolean }>(
      "/admin/users/gemini",
      { username, can_use_gemini: canUseGemini }
    ),
  adminSetTestingInsurers: (username: string, canUse: boolean) =>
    putJson<{ ok: boolean; can_use_testing_insurers: boolean }>(
      "/admin/users/testing-insurers",
      { username, can_use_testing_insurers: canUse }
    ),
  // Read-only: every saved match across insurers and books, current and removed.
  adminSavedMatches: () =>
    getJson<{ items: SavedMatchItem[]; history_bytes: number }>("/admin/saved-matches"),
  adminSetDisabled: (username: string, disabled: boolean) =>
    postJson<{ ok: boolean; disabled: boolean }>("/admin/users/disable", {
      username, disabled,
    }),
  adminDeleteUser: (username: string) =>
    postJson<{ ok: boolean; deleted: string }>("/admin/users/delete", {
      username,
    }),

  // `book` selects the reconciliation book; omitted means AP, so an existing call is
  // unchanged. 403 when the account does not hold that book, 422 when it is not a book.
  insurers: (book?: Book) =>
    getJson<{ insurers: InsurerInfo[]; book?: Book; hidden_testing?: number }>(
      book ? `/insurers?book=${encodeURIComponent(book)}` : "/insurers"
    ),

  // sheetOverride re-reads the span for a sheet the user picked instead of the
  // detected one — the span belongs to the SHEET, so the prefill must follow it.
  detectCri: (file: File, sheetOverride?: string, book?: Book) => {
    const fd = new FormData();
    fd.append("file", file);
    if (sheetOverride) fd.append("sheet_override", sheetOverride);
    if (book) fd.append("book", book);
    return postForm<{
      mode: string; sheet: string;
      // The CRI ledger's own date span, used to prefill the period filter.
      // null when the sheet carries no readable DOC DATE column.
      first_date: string | null; last_date: string | null;
      // AR only: the customer the statement's preamble names (blank key when unnamed).
      customer?: ArCustomer & { code: string };
    }>("/detect-cri", fd);
  },

  // AR: the customers already reconciled against this insurer, for the upload picker.
  arCustomers: (insurerName: string, book: Book) =>
    getJson<{ customers: ArCustomer[] }>(
      `/ar-customers?insurer_name=${encodeURIComponent(insurerName)}&book=${encodeURIComponent(book)}`
    ),

  listSheets: (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return postForm<{ sheets: string[] }>("/list-sheets", fd);
  },

  // `book` picks WHICH layout resolves the sheet. Load-bearing: AP and AR hold separate
  // layouts for one insurer, and book-blind this pre-filled nothing on AR (the AP Dubai
  // layout is multi_file, which offers no single sheet), leaving Run disabled.
  detectSoa: (insurerName: string, file: File, book?: Book) => {
    const fd = new FormData();
    fd.append("insurer_name", insurerName);
    if (book) fd.append("book", book);
    fd.append("file", file);
    return postForm<{ sheet: string }>("/detect-soa", fd);
  },

  // Which sheets of a prior-period unreconciled workbook the given side can read, with
  // the item count the run would actually carry (the server uses the engine's own
  // readers, so the number shown is the number that lands). Finance ships both books in
  // ONE workbook, so each side reads one sheet and reports the other as unusable —
  // `reason` carries why, for display beside the sheet.
  detectCarriedSheets: (side: "cri" | "insurer", insurerName: string, file: File) => {
    const fd = new FormData();
    fd.append("side", side);
    fd.append("insurer_name", insurerName);
    fd.append("file", file);
    // previous_output: the file is this app's own previous export (no sheets are listed).
    return postForm<{ sheets: CarriedSheet[]; previous_output?: boolean }>(
      "/detect-carried-sheets", fd);
  },

  // The insurer statement's own doc-date span. The as-of cutoff applies to BOTH books,
  // so the Run dialog needs this alongside the CRI span to say truthfully whether a
  // chosen month excludes anything. null dates when the sheet/column can't be read.
  // Takes a LIST so multi-file insurers (half the roster) are covered: the span is the
  // widest across every uploaded workbook. sheetName applies to the single-file path.
  insurerSpan: (insurerName: string, files: File[], sheetName?: string, book?: Book) => {
    const fd = new FormData();
    fd.append("insurer_name", insurerName);
    if (book) fd.append("book", book);
    for (const f of files) fd.append("files", f);
    if (sheetName) fd.append("sheet_name", sheetName);
    return postForm<{
      first_date: string | null; last_date: string | null;
    }>("/insurer-span", fd);
  },

  // Preview which uploaded sheets the multi-file merge will ingest (matched vs
  // ignored), mirroring the Streamlit "Sheets to merge" multiselect source.
  previewMulti: (insurerName: string, files: File[]) => {
    const fd = new FormData();
    fd.append("insurer_name", insurerName);
    for (const f of files) fd.append("files", f);
    return postForm<{ preview: MultiPreviewEntry[] }>("/preview-multi", fd);
  },

  reconcile: (args: {
    insurerName: string;
    insurerSheetName: string;
    // A LIST because CRI books some insurers under several INSURANCE COMPANY codes
    // (Watania: TAKAFUL GENERAL + TAKAFUL FAMILY) that reconcile as one account.
    // One file is the overwhelming case and posts exactly as before.
    criFiles: File[];
    insurerFiles: File[];
    // Which reconciliation book to run. Omitted means AP, so a caller that predates the
    // book axis posts exactly the run it always did. 403 when the account does not hold
    // the book, 422 when it is not a book at all — both BEFORE any parsing.
    book?: Book;
    settings: ReconSettings;
    bridgeRejections?: string[];
    // Multi-file "sheets to merge" narrowing: [filename, sheet] pairs. Sent only
    // when the user deselected some matched sheet (parity with Streamlit).
    insurerSheetFilter?: [string, string][];
    // Carries the in-session manual matches through a live-settings re-run.
    sessionPairs?: ManualMatchPair[];
    // Speculative pre-run: cache the prematch and return ONLY {prematch_id} (no
    // result). Lets the upload screen warm the cache during think-time so the real
    // Run is a cache hit. Same fingerprint, so it can never diverge from the real run.
    prewarm?: boolean;
    // Aborts an in-flight prewarm when an input changes and its result is moot.
    signal?: AbortSignal;
    // Client-generated id so /api/progress can be polled for this run's live stage.
    jobId?: string;
    // GL only: finance's carried-forward file of items still unreconciled from
    // earlier periods, in insurer-SOA format. Omitted on every run without it.
    unreconciledFile?: File | null;
    // Blank = let the engine scan every sheet and keep the one with the most items.
    // Set to pin one sheet, which finance's two-sided workbook needs: it holds the
    // other book's list too, and only the user can say which tab is which.
    unreconciledSheetName?: string;
    // GL only: the CRI-side carried-forward file. Not needed when unreconciledFile is
    // the previous period's output workbook, whose group tabs carry both books.
    unreconciledCriFile?: File | null;
    unreconciledCriSheetName?: string;
    // false ships the FIGURES only and leaves the transaction rows to api.frames().
    // The dashboard renders none of those rows until the statement view or a drill-down
    // is opened, and they are ~98.6% of the payload. Omitted (server default true) keeps
    // the single-response behaviour for any caller that wants everything at once.
    includeFrames?: boolean;
    // The display segment on screen; blank = All business. Dashboard re-runs only.
    segment?: string;
    // AR only: the customer picked on the upload page. Every re-run must re-send it, or
    // the server falls back to the customer the file names and reads another store.
    customerKey?: string;
    customerName?: string;
  }) => {
    const fd = new FormData();
    fd.append("insurer_name", args.insurerName);
    fd.append("insurer_sheet_name", args.insurerSheetName);
    // Sent only when set, so an AP run's request body is byte-identical to what it was
    // before the book axis existed (the server reads an absent book as AP).
    if (args.book) fd.append("book", args.book);
    for (const f of args.criFiles) fd.append("cri_file", f);
    for (const f of args.insurerFiles) fd.append("insurer_files", f);
    fd.append("tolerance", String(args.settings.tolerance));
    fd.append("amount_match_tol", String(args.settings.amount_match_tol));
    if (args.settings.amount_tol_premium !== undefined)
      fd.append("amount_tol_premium", String(args.settings.amount_tol_premium));
    if (args.settings.amount_tol_commission !== undefined)
      fd.append("amount_tol_commission", String(args.settings.amount_tol_commission));
    if (args.settings.usd_rate !== undefined)
      fd.append("usd_rate", String(args.settings.usd_rate));
    if (args.settings.eur_rate !== undefined)
      fd.append("eur_rate", String(args.settings.eur_rate));
    if (args.settings.mode_override) fd.append("mode_override", args.settings.mode_override);
    if (args.settings.cri_sheet_override)
      fd.append("cri_sheet_override", args.settings.cri_sheet_override);
    if (args.settings.period_from) fd.append("period_from", args.settings.period_from);
    // Only when the user CLEARED a From that had been offered/set. Tells the engine to
    // drop its cri_period_floor default so the run really covers the whole ledger — a
    // blank period_from alone cannot say this, since an untouched first load is blank
    // too and must keep the floor.
    if (args.settings.period_from_cleared) fd.append("period_from_cleared", "true");
    if (args.settings.period_to) fd.append("period_to", args.settings.period_to);
    // Omitted when blank so Automatic sends nothing at all (server default = no
    // override), keeping that path identical to a pre-feature run.
    if (args.settings.recon_month) fd.append("recon_month", args.settings.recon_month);
    // Repeated field: each rejected bridge signature. FastAPI reads list[str] = Form().
    for (const sig of args.bridgeRejections ?? []) fd.append("bridge_rejections", sig);
    if (args.insurerSheetFilter && args.insurerSheetFilter.length)
      fd.append("insurer_sheet_filter", JSON.stringify(args.insurerSheetFilter));
    if (args.sessionPairs && args.sessionPairs.length)
      fd.append("session_pairs", JSON.stringify(args.sessionPairs));
    if (args.includeFrames === false) fd.append("include_frames", "false");
    if (args.prewarm) fd.append("prewarm", "true");
    if (args.jobId) fd.append("job_id", args.jobId);
    if (args.unreconciledFile) fd.append("unreconciled_file", args.unreconciledFile);
    if (args.unreconciledSheetName)
      fd.append("unreconciled_sheet_name", args.unreconciledSheetName);
    if (args.unreconciledCriFile)
      fd.append("unreconciled_cri_file", args.unreconciledCriFile);
    if (args.unreconciledCriSheetName)
      fd.append("unreconciled_cri_sheet_name", args.unreconciledCriSheetName);
    // Explicit rather than the module value: a fresh upload must never inherit the
    // previous run's slice, while a re-run on the dashboard keeps the one on screen.
    if (args.segment) fd.append("segment", args.segment);
    if (args.customerKey) fd.append("customer_key", args.customerKey);
    if (args.customerName) fd.append("customer_name", args.customerName);
    return postForm<ReconcileResponse>("/reconcile", fd, args.signal);
  },

  // Live stage of an in-flight reconcile. {} means "no detail yet" (or finished) —
  // never an error condition; the caller just keeps showing its elapsed timer.
  progress: (jobId: string) =>
    getJson<{ stage?: string; index?: number; total?: number }>(
      "/progress?job_id=" + encodeURIComponent(jobId)
    ),

  finalize: (args: {
    prematchId: string;
    insurerName: string;
    sessionPairs?: ManualMatchPair[];
    manualNotesCri?: Record<string, string>;
    manualNotesInsurer?: Record<string, string>;
    // false = skip the (large) frames in the response — for math-inert edits
    // (manual notes) where the client already has everything it shows.
    includeFrames?: boolean;
    // true = return only the manually-matched rows (df_cri_delta/df_insurer_delta)
    // instead of the full frames, for the manual-match apply path. Every figure is
    // still recomputed server-side; only the unchanged ~96k rows are omitted. The
    // caller must patch them in via lib/framesDelta.applyFramesDelta.
    framesDelta?: boolean;
  }) =>
    postJson<FinalizeResponse>("/finalize", {
      prematch_id: args.prematchId,
      insurer_name: args.insurerName,
      session_pairs: args.sessionPairs ?? [],
      manual_notes_cri: args.manualNotesCri ?? null,
      manual_notes_insurer: args.manualNotesInsurer ?? null,
      include_frames: args.includeFrames ?? true,
      frames_delta: args.framesDelta ?? false,
      ...segmentBody(),
    }),

  // The two transaction frames for an already-computed reconciliation, fetched apart
  // from its figures. Takes the same identifying inputs as finalize() because it has to
  // finalize to the same result before serialising — the rows it returns are produced by
  // the same server-side path as an inline response, so they are interchangeable.
  frames: (args: {
    prematchId: string;
    insurerName: string;
    sessionPairs?: ManualMatchPair[];
    manualNotesCri?: Record<string, string>;
    manualNotesInsurer?: Record<string, string>;
  }) =>
    postJson<{ df_cri: FrameRecords; df_insurer: FrameRecords }>("/frames", {
      prematch_id: args.prematchId,
      insurer_name: args.insurerName,
      session_pairs: args.sessionPairs ?? [],
      manual_notes_cri: args.manualNotesCri ?? null,
      manual_notes_insurer: args.manualNotesInsurer ?? null,
      ...segmentBody(),
    }),

  manualMatches: (insurerName: string) =>
    getJson<{ pairs: ManualMatchPair[] }>(
      "/manual-matches?insurer_name=" + encodeURIComponent(insurerName) + scopeQs()
    ),

  // Persist a pair to the per-insurer store so it auto-applies on the next upload
  // (the same durable content-signature store the Streamlit app wrote).
  // Sends whichever signature fields the entry carries, so an N:M group
  // ({cri_sigs, insurer_sigs}), a same-book offset ({a_sig, b_sig}) and a 1:1
  // cross-book match ({cri_sig, insurer_sig}) are each stored as their own kind.
  // `replace`: older saved matches finance chose to replace in the save dialog; they are
  // removed in the same write that adds this one.
  addManualMatch: (insurerName: string, pair: ManualMatchPair, replace: ManualMatchPair[] = []) =>
    postJson<{ pairs: ManualMatchPair[] }>("/manual-matches", {
      insurer_name: insurerName, ...pair, replace, ...scopeBody(),
    }),

  // What the save dialog must ask before these pairs are saved (conflicts, look-alikes),
  // read on the run's own frames so it sees every row, not just the page on screen.
  precheckManualMatches: (prematchId: string, insurerName: string, pairs: ManualMatchPair[]) =>
    postJson<{ checks: SaveCheck[] }>("/manual-matches/precheck", {
      prematch_id: prematchId, insurer_name: insurerName, pairs,
    }),

  removeManualMatches: (insurerName: string, pairs: ManualMatchPair[]) =>
    deleteJson<{ pairs: ManualMatchPair[] }>("/manual-matches", {
      insurer_name: insurerName, pairs, ...scopeBody(),
    }),

  // Released (un-matched) auto-matches. The matcher sometimes pairs two rows that don't
  // belong together, and the manual-match grids only offer UNMATCHED rows — so without
  // this a wrongly matched transaction could never be re-paired. Releasing returns BOTH
  // legs to the open buckets. The Difference does not move (the legs land in buckets
  // that cancel in the summary identity), and it is fully reversible.
  releasedMatches: (insurerName: string) =>
    getJson<{ released: ReleasedGroup[] }>(
      "/released-matches?insurer_name=" + encodeURIComponent(insurerName) + scopeQs()
    ),

  releaseMatch: (insurerName: string, sigs: string[], reason = "") =>
    postJson<{ released: ReleasedGroup[] }>("/released-matches", {
      insurer_name: insurerName, sigs, reason, ...scopeBody(),
    }),

  restoreMatch: (insurerName: string, sigs: string[]) =>
    deleteJson<{ released: ReleasedGroup[] }>("/released-matches", {
      insurer_name: insurerName, sigs, ...scopeBody(),
    }),

  // Rejected prior-period auto-clears. The tier ladder clears carried items on its own
  // and that moves the Adjusted Closing Balance, so a wrong clear must be correctable —
  // and the correction persists, keyed on the row's durable content signature so it
  // survives next month's re-upload. Fully reversible: restore drops the override and
  // the item auto-clears again.
  unreconciledRejections: (insurerName: string) =>
    getJson<{ rejections: Record<string, string> }>(
      "/unreconciled-rejections?insurer_name=" + encodeURIComponent(insurerName)
      + scopeQs()
    ),

  // `reason` is optional and the UI does not collect one: rejecting is a single click,
  // and the override itself is the record. The field stays on the wire because the
  // store persists it and an older entry may carry one.
  // `list` names the carried list the item is on: the two lists' signatures are computed
  // independently, so the same signature on each is two different items.
  rejectUnreconciledClear: (insurerName: string, sig: string, reason = "",
                            list?: "cri" | "insurer") =>
    postJson<{ rejections: Record<string, string> }>("/unreconciled-rejections", {
      insurer_name: insurerName, sig, reason, list, ...scopeBody(),
    }),

  restoreUnreconciledClear: (insurerName: string, sig: string, list?: "cri" | "insurer") =>
    deleteJson<{ rejections: Record<string, string> }>("/unreconciled-rejections", {
      insurer_name: insurerName, sig, list, ...scopeBody(),
    }),

  // Manual matches on prior-period CARRIED items. Deliberately a separate endpoint from
  // /manual-matches: a carried item is not a row of this period's ledger, so pairing it
  // must NOT stamp the engine frames (that would move insurer_closing and the
  // Difference). These pairs reach only the Adjusted Closing Balance. A pair that does
  // not tie settles nothing and auto-annotates the item with the gap instead.
  carriedMatches: (insurerName: string) =>
    getJson<{ pairs: CarriedMatchPair[] }>(
      "/carried-matches?insurer_name=" + encodeURIComponent(insurerName) + scopeQs()
    ),

  // `position` puts a pair back where it was (Undo): the first saved pair naming an item
  // wins, so order is part of what a pair does.
  addCarriedMatch: (insurerName: string, pair: CarriedMatchPair, position?: number) =>
    postJson<{ pairs: CarriedMatchPair[] }>("/carried-matches", {
      insurer_name: insurerName, ...pair, position, ...scopeBody(),
    }),

  removeCarriedMatches: (insurerName: string, pairs: CarriedMatchPair[]) =>
    deleteJson<{ pairs: CarriedMatchPair[] }>("/carried-matches", {
      insurer_name: insurerName, pairs, ...scopeBody(),
    }),

  // Finance's notes on prior-period items, keyed by carried list + signature. Math-inert.
  carriedNotes: (insurerName: string) =>
    getJson<{ notes: CarriedNote[] }>(
      "/carried-notes?insurer_name=" + encodeURIComponent(insurerName) + scopeQs()
    ),

  setCarriedNote: (insurerName: string, list: "cri" | "insurer", sig: string, note: string) =>
    postJson<{ notes: CarriedNote[] }>("/carried-notes", {
      insurer_name: insurerName, list, sig, note, ...scopeBody(),
    }),

  // Pinned table columns. Scope is the insurer, EXCEPT the CRI ledger (insurerName
  // omitted) which shares one scope across all insurers — same export format. The
  // user is resolved server-side from the session, never sent by the client.
  // `paired` selects the Auto Matched grid's own scope (composite cri::/ins:: keys),
  // kept separate from the per-side statement scopes.
  columnPins: (insurerName?: string, paired = false) => {
    const q = new URLSearchParams();
    if (insurerName) q.set("insurer_name", insurerName);
    if (paired) q.set("paired", "1");
    const s = q.toString();
    return getJson<{ columns: string[] }>("/column-pins" + (s ? "?" + s : ""));
  },

  setColumnPins: (insurerName: string | undefined, columns: string[], paired = false) =>
    putJson<{ columns: string[] }>("/column-pins", {
      insurer_name: insurerName ?? "", columns, paired,
    }),

  // Saved USD/EUR rates for THIS user, across sessions. A currency missing from
  // `saved` means nothing is stored and the matching `defaults` value applies — an
  // absent rate is never zero. The user is resolved server-side from the session.
  fxRates: () => getJson<{
    saved: { usd?: number; eur?: number };
    defaults: { usd: number; eur: number };
  }>("/fx-rates"),

  // Merge one or both rates. Pass null for a currency to FORGET it (revert to default).
  setFxRates: (rates: { usd?: number | null; eur?: number | null }) =>
    putJson<{ saved: { usd?: number; eur?: number } }>("/fx-rates", rates),

  // Export returns a binary xlsx; caller triggers a browser download from the blob.
  exportWorkbook: async (args: {
    prematchId: string;
    insurerName: string;
    settings: ReconSettings;
    sessionPairs?: ManualMatchPair[];
    manualNotesCri?: Record<string, string>;
    manualNotesInsurer?: Record<string, string>;
  }): Promise<Blob> => {
    const res = await fetch(BASE + "/export", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        prematch_id: args.prematchId,
        insurer_name: args.insurerName,
        settings: args.settings,
        session_pairs: args.sessionPairs ?? [],
        manual_notes_cri: args.manualNotesCri ?? null,
        manual_notes_insurer: args.manualNotesInsurer ?? null,
        ...segmentBody(),
      }),
    });
    return blobOrThrow(res);
  },

  // Step 1: the customer roll-forward between a previous and a current statement.
  customerRollforward: (args: {
    book: Book;
    insurerName: string;
    previousFiles: File[];
    currentFiles: File[];
    previousMonth: string;   // "YYYY-MM"
    periodTo: string;        // "YYYY-MM-DD"
    tolerance: number;
    usdRate: number;
    eurRate: number;
  }): Promise<RollforwardResponse> => {
    const form = new FormData();
    form.append("book", args.book);
    form.append("insurer_name", args.insurerName);
    args.previousFiles.forEach((f) => form.append("previous_files", f));
    args.currentFiles.forEach((f) => form.append("current_files", f));
    form.append("previous_month", args.previousMonth);
    form.append("period_to", args.periodTo);
    form.append("tolerance", String(args.tolerance));
    form.append("usd_rate", String(args.usdRate));
    form.append("eur_rate", String(args.eurRate));
    return postForm<RollforwardResponse>("/customer-rollforward", form);
  },

  exportCustomerRollforward: async (rollforwardId: string, book: Book): Promise<Blob> => {
    const form = new FormData();
    form.append("rollforward_id", rollforwardId);
    form.append("book", book);
    const res = await fetch(BASE + "/customer-rollforward/export",
                            { method: "POST", credentials: "include", body: form });
    return blobOrThrow(res);
  },

  // Build the annotated workbook SPECULATIVELY, so the download is instant. Same body as
  // exportWorkbook; returns no bytes — the workbook is left in the server-side cache,
  // keyed on a digest of the finalized result, for whichever worker serves the download.
  //
  // Safe to call at any time and as often as you like: a workbook is only ever reused
  // when that digest matches, so a pre-build that a later edit supersedes is not stale,
  // it is simply never served.
  prebuildExport: (args: {
    prematchId: string;
    insurerName: string;
    settings: ReconSettings;
    sessionPairs?: ManualMatchPair[];
    manualNotesCri?: Record<string, string>;
    manualNotesInsurer?: Record<string, string>;
  }) =>
    postJson<{ ready: boolean; cached?: boolean }>("/prebuild-export", {
      prematch_id: args.prematchId,
      insurer_name: args.insurerName,
      settings: args.settings,
      session_pairs: args.sessionPairs ?? [],
      manual_notes_cri: args.manualNotesCri ?? null,
      manual_notes_insurer: args.manualNotesInsurer ?? null,
      ...segmentBody(),
    }),

  // Summary CSV (mirrors the Streamlit "Download Summary CSV").
  exportCsv: async (args: {
    prematchId: string;
    insurerName: string;
    settings: ReconSettings;
    sessionPairs?: ManualMatchPair[];
    manualNotesCri?: Record<string, string>;
    manualNotesInsurer?: Record<string, string>;
  }): Promise<Blob> => {
    const res = await fetch(BASE + "/export-csv", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        prematch_id: args.prematchId,
        insurer_name: args.insurerName,
        settings: args.settings,
        session_pairs: args.sessionPairs ?? [],
        manual_notes_cri: args.manualNotesCri ?? null,
        manual_notes_insurer: args.manualNotesInsurer ?? null,
        ...segmentBody(),
      }),
    });
    return blobOrThrow(res);
  },
};

export type { ReconResult };
